import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
  addDoc
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Container, Card, Collapse } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaStar,
  FaTimes,
  FaClock,
  FaMapMarkerAlt,
  FaCheck,
  FaPencilAlt
} from 'react-icons/fa';
import { PiBackpackBold } from 'react-icons/pi';
import { RiDirectionLine } from 'react-icons/ri';
import { BiCommentDetail } from 'react-icons/bi';
import { Button as MuiButton, TextField, Grid } from '@mui/material';
import './ChastlizettelPublic.css';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const formatMitnahmen = (mitnahmen) => {
  if (Array.isArray(mitnahmen)) return mitnahmen;
  if (typeof mitnahmen === 'string') {
    return mitnahmen
      .split(/,|\sund\s/g)
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }
  return ['Zvieri', 'dem Wetter angepasste Kleidung'];
};

const ChastlizettelPublic = () => {
  // Gruppenname z. B. "Smarties", "Caramba", "Taketazu", etc.
  const { group } = useParams();

  const [chastlizettel, setChastlizettel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pfadiname, setPfadiname] = useState('');
  const [regComment, setRegComment] = useState('');
  const [action, setAction] = useState('anmelden');
  const [lastAction, setLastAction] = useState('anmelden');

  // Abonniere den Chastlizettel via Firestore-Realtime Listener
  useEffect(() => {
    const q = query(
      collection(db, 'chastlizettel'),
      where('selectedGroups', 'array-contains', group),
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        const now = new Date();
        let endTime = null;
        if (data.verabschiedungsTimestamp) {
          endTime = data.verabschiedungsTimestamp.toDate();
        } else if (data.besammlungsDatum) {
          endTime = new Date(`${data.besammlungsDatum}T23:59:59`);
        }
        const sixHoursPassed = endTime && (now - endTime) >= 6 * 3600 * 1000;
        if (sixHoursPassed) {
          setChastlizettel({ ...data, activityType: 'planung' });
        } else {
          setChastlizettel({ id: doc.id, ...data });
        }
      }
      setLoading(false);
    }, (error) => {
      console.error("Fehler beim Laden des Chastlizettels:", error);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [group]);

  // Sende nach jedem Render (oder Änderung) die neue Höhe per postMessage an den Parent
  useEffect(() => {
    function sendeNeueHoehe() {
      // Gesamthöhe des Dokuments ermitteln
      const neueHoehe = document.documentElement.scrollHeight;
      // iframeId: "cae_iframe_" + Gruppenname in Kleinbuchstaben
      const iframeId = 'cae_iframe_' + group.toLowerCase();
      window.parent.postMessage({
        type: 'cae-resize',
        iframeId: iframeId,
        height: neueHoehe
      }, '*');
    }
    sendeNeueHoehe();
  }, [group, chastlizettel, showRegistration, registrationSuccess]);

  const noActivity =
    chastlizettel &&
    chastlizettel.activityType &&
    chastlizettel.activityType.toLowerCase() === 'keine aktivitaet';

  const isPlanning =
    chastlizettel &&
    chastlizettel.activityType &&
    chastlizettel.activityType === 'planung';

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (!chastlizettel || !chastlizettel.id) {
      setErrorMsg("Kein aktives Chastlizettel gefunden.");
      return;
    }
    const aktivitaetsDatum = chastlizettel.startDatum || chastlizettel.besammlungsDatum;
    const regData = {
      firstName,
      lastName,
      pfadiname,
      action,
      comment: regComment,
      timestamp: new Date(),
      aktivitaetsDatum,
      activityId: chastlizettel.id,
      group,
    };
    try {
      await addDoc(
        collection(db, 'chastlizettel', chastlizettel.id, 'registrations'),
        regData
      );
      setLastAction(action);
      setRegistrationSuccess(true);
      setTimeout(() => {
        setFirstName('');
        setLastName('');
        setPfadiname('');
        setAction('anmelden');
        setRegComment('');
        setShowRegistration(false);
        setRegistrationSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Fehler beim Speichern der Anmeldung:", error);
      setErrorMsg("Fehler beim Speichern der Anmeldung.");
    }
  };

  if (loading)
    return (
      <Container fluid style={{ padding: 0, margin: 0, width: '100%', background: 'transparent' }}>
        <p>Lade Chastlizettel …</p>
      </Container>
    );

  let headerIcon, headerTitle;
  if (noActivity) {
    headerIcon = <FaTimes className="header-icon" />;
    headerTitle = "Keine Aktivität";
  } else if (chastlizettel.activityType && chastlizettel.activityType.toLowerCase() === 'anlass') {
    headerIcon = <FaStar className="header-icon" />;
    headerTitle = "ANLASS";
  } else if (isPlanning) {
    headerIcon = <FaPencilAlt className="planning-header-icon" />;
    headerTitle = "Chastlizettel";
  } else {
    headerIcon = <FaCalendarAlt className="header-icon" />;
    headerTitle = "AKTIVITÄT";
  }

  const startDatum = chastlizettel.startDatum || chastlizettel.besammlungsDatum;
  const endDatum = chastlizettel.verabschiedungsDatum || chastlizettel.besammlungsDatum;

  return (
    <Container fluid style={{ padding: 0, margin: 0, width: '100%', background: 'transparent' }}>
      <div className="chast-header">
        <div className="chast-header-content">
          {headerIcon}
          <span className="header-title">{headerTitle}</span>
          {!isPlanning && startDatum && endDatum && (
            startDatum !== endDatum ? (
              <span className="header-date">{formatDate(startDatum)} – {formatDate(endDatum)}</span>
            ) : (
              <span className="header-date">{formatDate(startDatum)}</span>
            )
          )}
        </div>
      </div>

      <Card className="chast-card" style={{ 
          backgroundColor: 'white', 
          border: '1px solid #ddd', 
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          height: 'auto', 
          overflowY: 'visible'
        }}>
        <Card.Body style={{ position: 'relative', padding: showRegistration ? '50px 20px 70px 20px' : '20px' }}>
          {noActivity ? (
            <div className="no-activity-box">
              <div className="no-activity-icon">
                <FaTimes />
              </div>
              <div className="no-activity-text">
                Diesen Samstag findet keine Aktivität statt
              </div>
              {chastlizettel.kommentar && (
                <div className="no-activity-comment">{chastlizettel.kommentar}</div>
              )}
            </div>
          ) : isPlanning ? (
            <div className="no-activity-box">
              <div className="planning-icon">
                <FaPencilAlt />
              </div>
              <div className="planning-text">
                Die nächste Aktivität befindet sich noch in Planung
              </div>
            </div>
          ) : (
            <>
              <Collapse in={!showRegistration}>
                <div>
                  {startDatum && endDatum && startDatum !== endDatum && (
                    <div className="timeline-dates-wrapper">
                      <div className="time-start-date-box">{formatDate(startDatum)}</div>
                      <div className="time-end-date-box">{formatDate(endDatum)}</div>
                    </div>
                  )}
                  <div className="chast-timeline">
                    <div className="timeline-bar">
                      <div className="time-section">
                        <FaClock className="clock-icon" />
                        <span className="time-start">{chastlizettel.besammlungsUhrzeit || '14:00'}</span>
                      </div>
                      <div className="time-section">
                        <FaClock className="clock-icon" />
                        <span className="time-end">{chastlizettel.verabschiedungsUhrzeit || '16:00'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="location-row">
                    <div className="location-field">
                      {chastlizettel.treffpunktBes && chastlizettel.treffpunktVerabsch &&
                        chastlizettel.treffpunktBes !== chastlizettel.treffpunktVerabsch && (
                          <span className="location-label">Besammlung</span>
                        )}
                      <div className="field-title">
                        <FaMapMarkerAlt className="location-title-icon" />
                      </div>
                      <a
                        href={
                          chastlizettel.treffpunktBesLink ||
                          `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(chastlizettel.treffpunktBes || 'Sirihuus')}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="location-box"
                      >
                        <span>{chastlizettel.treffpunktBes || 'Sirihuus'}</span>
                        <RiDirectionLine className="direction-icon" />
                      </a>
                    </div>
                    {chastlizettel.treffpunktVerabsch &&
                      chastlizettel.treffpunktVerabsch !== chastlizettel.treffpunktBes && (
                        <div className="location-field">
                          <span className="location-label">Verabschiedung</span>
                          <div className="field-title">
                            <FaMapMarkerAlt className="location-title-icon" />
                          </div>
                          <a
                            href={
                              chastlizettel.treffpunktVerabschLink ||
                              `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(chastlizettel.treffpunktVerabsch)}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="location-box"
                          >
                            <span>{chastlizettel.treffpunktVerabsch}</span>
                            <RiDirectionLine className="direction-icon" />
                          </a>
                        </div>
                    )}
                  </div>
                  <div className="mitnehmen-field">
                    <div className="field-title">
                      <PiBackpackBold className="mitnehmen-icon" />
                    </div>
                    <div className="items-container">
                      {formatMitnahmen(chastlizettel.selectedMitnahmen || chastlizettel.selectedMitnehmen)
                        .map((item, idx) => (
                          <span key={idx} className="item-box">{item}</span>
                        ))}
                    </div>
                  </div>
                  {chastlizettel.kommentar && (
                    <div className="comment-section">
                      <div className="field-title">
                        <BiCommentDetail className="comment-icon" />
                      </div>
                      <div className="comment-box">{chastlizettel.kommentar}</div>
                    </div>
                  )}
                </div>
              </Collapse>
              <Collapse in={showRegistration}>
                <div style={{ padding: '5px' }}>
                  <div className="registration-form-footer">
                    <div className="back-button" onClick={() => setShowRegistration(false)}>
                      Zurück
                    </div>
                  </div>
                  <div className="slider-container">
                    <div
                      className={`slider-button ${action === 'anmelden' ? 'selected anmelden' : ''}`}
                      onClick={() => setAction('anmelden')}
                    >
                      <FaCheck className="slider-icon" /> Anmelden
                    </div>
                    <div
                      className={`slider-button ${action === 'abmelden' ? 'selected abmelden' : ''}`}
                      onClick={() => setAction('abmelden')}
                    >
                      <FaTimes className="slider-icon" /> Abmelden
                    </div>
                  </div>
                  <form onSubmit={handleRegistrationSubmit}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <TextField
                          label="Vorname"
                          variant="standard"
                          fullWidth
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Nachname"
                          variant="standard"
                          fullWidth
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          required
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Pfadiname"
                          variant="standard"
                          fullWidth
                          value={pfadiname}
                          onChange={(e) => setPfadiname(e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Kommentar"
                          variant="standard"
                          fullWidth
                          multiline
                          rows={3}
                          value={regComment}
                          onChange={(e) => setRegComment(e.target.value)}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                    <div className="form-submit">
                      <MuiButton variant="contained" type="submit" size="small">
                        Absenden
                      </MuiButton>
                    </div>
                  </form>
                </div>
              </Collapse>
              {!showRegistration && (
                <div
                  className="registration-toggle"
                  style={{
                    marginTop: '20px',
                    marginBottom: '5px',
                    textAlign: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowRegistration(true)}
                >
                  von der Aktivität An/Abmelden
                </div>
              )}
              {errorMsg && <div style={{ marginTop: '1rem', color: 'red' }}>{errorMsg}</div>}
              {registrationSuccess && (
                <div className="registration-success-overlay">
                  {lastAction === 'anmelden' ? (
                    <>
                      <FaCheck className="success-icon" />
                      <div className="success-text">Deine Anmeldung wurde gespeichert.</div>
                    </>
                  ) : (
                    <>
                      <FaTimes className="error-icon" />
                      <div className="success-text">Deine Abmeldung wurde gespeichert.</div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ChastlizettelPublic;